import RecordLabel from "components/RecordLabel";
import { getBundlesById } from "containers/Bundles/Bundles.selectors";
import { getProjectsById } from "containers/Projects/Projects.selectors";
import { getWorkspaceData } from "containers/Workspaces/Workspaces.selectors";
import { Bundle, ContainerType, Project, Workspace } from "graphql/generated/graphql";
import qs from "qs";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useLocation, useRouteMatch } from "react-router";
import { mapScreensToPaths } from "utils/RouterManager/utils";
import { getRelativeUrl, getWorkspaceUrl } from "utils/service";

interface MatchParams {
    locationType: Lowercase<ContainerType>;
}

export const useCurrentContainer = () => {
    const location = useLocation();
    const queryParams = qs.parse(location.search.substring(1));

    const pathMatch = useRouteMatch<MatchParams>("/:locationType");
    const locationType = pathMatch?.params?.locationType;

    const idFromQuery = queryParams.id as string | undefined;
    const nameFromQuery = queryParams.name as string | undefined;

    const projectsById: Record<string, Project> = useSelector(getProjectsById);
    const bundlesById: Record<string, Bundle> = useSelector(getBundlesById);
    const workspaceData: Workspace = useSelector(getWorkspaceData);

    // Always return an ID so callers that look up Redux records by ID work correctly.
    let locationId = idFromQuery;
    if (!locationId && nameFromQuery) {
        if (locationType === "project") {
            locationId = Object.values(projectsById).find((p) => p.name === nameFromQuery)?.id;
        } else if (locationType === "bundle") {
            locationId = Object.values(bundlesById).find((b) => b.name === nameFromQuery)?.id;
        } else if (locationType === "workspace") {
            locationId = workspaceData?.name === nameFromQuery ? workspaceData.id : undefined;
        }
    }
    if (!locationId && locationType === "workspace") {
        locationId = workspaceData?.id;
    }

    return { locationType, locationId };
};

export const useBreadcrumbs = () => {
    const { locationType, locationId } = useCurrentContainer();
    const workspace: Workspace = useSelector(getWorkspaceData);

    const workspaceBreadcrumbs = useMemo(
        () => [
            workspace
                ? {
                      link: getWorkspaceUrl(),
                      label: (
                          <RecordLabel
                              name={workspace.name}
                              image={workspace.icon}
                              iconByType={"workspace"}
                          />
                      ),
                  }
                : {
                      link: "/",
                      label: <RecordLabel name="Workspace" iconByType={"workspace"} />,
                  },
        ],
        [workspace]
    );

    const userRoleManagementBreadcrumbs = useMemo(
        () => [
            ...workspaceBreadcrumbs,
            { link: mapScreensToPaths.userRoleManagement, label: "User & Role Management" },
        ],
        [workspaceBreadcrumbs]
    );

    const projectsById: Record<string, Project> = useSelector(getProjectsById);
    const bundlesById: Record<string, Bundle> = useSelector(getBundlesById);
    const containerData = !locationId
        ? null
        : locationType === "project"
        ? projectsById[locationId]
        : locationType === "bundle"
        ? bundlesById[locationId]
        : null;

    const containerBreadcrumbs = useMemo(
        () =>
            containerData && locationType && locationId
                ? [
                      {
                          link: getRelativeUrl(locationType, containerData.name, "name"),
                          label: (
                              <RecordLabel
                                  name={containerData?.name}
                                  image={containerData?.icon}
                                  iconByType={locationType}
                              />
                          ),
                      },
                  ]
                : workspaceBreadcrumbs,
        [containerData, locationType, locationId, workspaceBreadcrumbs]
    );

    return { workspaceBreadcrumbs, userRoleManagementBreadcrumbs, containerBreadcrumbs };
};
